/**
 * Wormhole data constants for Pathfinder-style UI
 */

// Wormhole type to destination class mapping
// Based on EVE Online wormhole connection types
export const WORMHOLE_DESTINATIONS: Record<string, string> = {
  // K162 - reverse/exit wormhole (unknown origin)
  K162: 'Unknown',

  // Wormholes leading TO specific wormhole classes
  H121: 'C1',
  C125: 'C2',
  O883: 'C3',
  M609: 'C4',
  L614: 'C5',
  S199: 'C6',

  // C1 statics
  Z971: 'C1',

  // C2 statics
  D382: 'C2',
  N766: 'C2',

  // C3 statics
  O477: 'C3',
  M267: 'C3',

  // C4 statics
  Y683: 'C4',
  X877: 'C4',

  // C5 statics
  N432: 'C5',
  H900: 'C5',

  // C6 statics
  W237: 'C6',
  V911: 'C6',

  // Highsec statics
  N110: 'HS',
  B274: 'HS',
  A239: 'LS',
  D792: 'HS',

  // Lowsec statics
  J244: 'LS',
  U210: 'LS',
  N968: 'LS',

  // Nullsec statics
  Z060: 'NS',
  K346: 'NS',
  Z457: 'NS',
  V301: 'NS',
  V283: 'NS',
  R943: 'NS',
  S047: 'NS',

  // Thera connections
  L031: 'Thera',
  M164: 'Thera',
  Q317: 'Thera',
  V898: 'Thera',
  G008: 'Thera',
  C414: 'Thera',
  A009: 'Thera',

  // Drifter wormhole connections
  S877: 'Drifter',
  E587: 'Drifter',
  V928: 'Drifter',

  // Pochven connections
  F216: 'Pochven',
  C729: 'Pochven',
  R081: 'Pochven',
  U372: 'Pochven',
  X450: 'Pochven',
};

// Color scheme for wormhole class/destination text (Pathfinder-style)
export const WH_CLASS_COLORS: Record<string, string> = {
  // Wormhole classes - gradient from safe (green) to dangerous (red)
  C1: '#48bb78', // Green - Class 1
  C2: '#4299e1', // Blue - Class 2
  C3: '#ed8936', // Orange - Class 3
  C4: '#f56565', // Red - Class 4
  C5: '#e53e3e', // Dark Red - Class 5
  C6: '#9b2c2c', // Maroon - Class 6

  // K-space security colors
  HS: '#22c55e', // Bright Green (Highsec)
  H: '#22c55e', // Alias for Highsec
  LS: '#eab308', // Yellow (Lowsec)
  L: '#eab308', // Alias for Lowsec
  NS: '#ef4444', // Red (Nullsec)
  N: '#ef4444', // Alias for Nullsec

  // Special wormhole types
  Thera: '#a855f7', // Purple - Thera
  Drifter: '#6366f1', // Indigo - Drifter wormholes
  Pochven: '#ec4899', // Pink - Pochven (Triglavian)
  C13: '#8b5cf6', // Violet - Shattered wormholes

  // Unknown/default
  Unknown: '#6b7280', // Gray
};

// Get the color for a wormhole class or destination
export function getWormholeColor(classOrDest: string | null | undefined): string {
  if (!classOrDest) return WH_CLASS_COLORS.Unknown;
  return WH_CLASS_COLORS[classOrDest] || WH_CLASS_COLORS.Unknown;
}

// Get the destination class from a wormhole type code
export function getWormholeDestination(whType: string | null | undefined): string | null {
  if (!whType) return null;
  return WORMHOLE_DESTINATIONS[whType] || null;
}

// Format statics for display (e.g., ["C3", "LS"] -> "C3 L")
export function formatStatics(statics: string[] | null | undefined): string {
  if (!statics || statics.length === 0) return '';

  return statics
    .map((s) => {
      // Shorten HS/LS/NS to H/L/N for compact display
      if (s === 'HS') return 'H';
      if (s === 'LS') return 'L';
      if (s === 'NS') return 'N';
      return s;
    })
    .join(' ');
}

// Wormhole effect icons (emoji-based)
export const WORMHOLE_EFFECT_ICONS: Record<string, string> = {
  'Wolf-Rayet': '☀️',
  'Black Hole': '🕳️',
  Pulsar: '💫',
  Magnetar: '🧲',
  'Red Giant': '🔴',
  'Cataclysmic Variable': '💥',
};

// Get effect icon
export function getEffectIcon(effect: string | null | undefined): string | null {
  if (!effect) return null;
  return WORMHOLE_EFFECT_ICONS[effect] || '✨';
}

/**
 * Wormhole statics lookup by system ID (fallback)
 *
 * NOTE: Statics data is now stored in the backend database (eve_universe_cache.static_data).
 * This lookup is only used as a fallback when the backend doesn't return statics.
 * Data imported from Pathfinder's system_static.csv via migrations/0004_statics_data.sql
 *
 * Format: systemId -> [destination1, destination2, ...]
 */
export const WORMHOLE_STATICS_LOOKUP: Record<number, string[]> = {
  // Sample systems (fallback only - real data in database)
  31000546: ['C1', 'C4'], // J120442
};

/**
 * Get statics for a wormhole system by its EVE system ID
 * Returns null if no statics data is available
 */
export function getStaticsForSystem(systemId: number): string[] | null {
  return WORMHOLE_STATICS_LOOKUP[systemId] || null;
}
